import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, Loader2, Check, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { PageHero, Reveal, Eyebrow } from "@/components/bits";
import { setMetaTags } from "@/seo";

const inputCls =
  "w-full bg-white border border-tnn-ink/12 rounded-xl px-4 py-3 text-sm text-tnn-ink placeholder:text-tnn-muted/60 focus:outline-none focus:border-tnn-purple focus:ring-2 focus:ring-tnn-purple/15 transition-[border-color,box-shadow] duration-200";
const labelCls = "block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2";

const RECOMMEND = [
  { key: "yes", label: "Yes, definitely" },
  { key: "maybe", label: "Maybe" },
  { key: "no", label: "Not really" },
];

export default function Feedback() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    rating: 0,
    most_valuable: "",
    improve: "",
    recommend: "",
    testimonial: "",
    testimonial_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setMetaTags({
      title: "Masterclass Feedback | The Nutrition Network Africa",
      description: "Share your feedback on the Positioning for Global Leadership masterclass.",
      path: "/feedback",
    });
  }, []);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error("Please tap a star rating first.");
      return;
    }
    if (!form.recommend) {
      toast.error("Please tell us if you would recommend the masterclass.");
      return;
    }
    if (!form.most_valuable.trim()) {
      toast.error("Please share the most valuable part of the session.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/feedback", {
        ...form,
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        improve: form.improve.trim() || null,
        testimonial: form.testimonial.trim() || null,
      });
      setSubmitted(true);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="feedback-page">
      <PageHero
        testid="feedback-hero"
        eyebrow="Positioning for Global Leadership"
        title={
          <>
            How was the <span className="text-tnn-purple">masterclass?</span>
          </>
        }
        lead="Two minutes of your honest thoughts shapes what we build next. Thank you for being part of it."
      />

      <section className="bg-tnn-warm pb-20 lg:pb-28">
        <div className="max-w-2xl mx-auto px-6">
          <Reveal delay={0.08}>
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-tnn-ink/8 rounded-3xl p-12 text-center"
                data-testid="feedback-success"
              >
                <span className="w-14 h-14 rounded-full bg-tnn-soft inline-flex items-center justify-center">
                  <PartyPopper className="w-6 h-6 text-tnn-purple" />
                </span>
                <h2 className="mt-5 font-display text-2xl font-bold text-tnn-ink">Thank you.</h2>
                <p className="mt-2 text-sm text-tnn-muted leading-relaxed max-w-sm mx-auto">
                  Your feedback is in. We read every response personally, and it directly shapes the next session.
                </p>
              </motion.div>
            ) : (
              <form onSubmit={onSubmit} className="bg-white border border-tnn-ink/8 rounded-3xl p-8 lg:p-10 space-y-7" data-testid="feedback-form">
                <div>
                  <label className={labelCls}>How would you rate it overall? *</label>
                  <div className="flex gap-2" data-testid="feedback-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, rating: n }))}
                        data-testid={`feedback-star-${n}`}
                        aria-label={`${n} star${n > 1 ? "s" : ""}`}
                        className="p-1.5 transition-transform duration-150 hover:scale-110"
                      >
                        <Star
                          className={`w-8 h-8 transition-colors duration-150 ${
                            form.rating >= n ? "text-tnn-purple fill-tnn-purple" : "text-tnn-ink/20"
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback-valuable" className={labelCls}>What was the most valuable part? *</label>
                  <textarea
                    id="feedback-valuable"
                    rows={3}
                    value={form.most_valuable}
                    onChange={set("most_valuable")}
                    placeholder="The session, idea or moment that stayed with you"
                    data-testid="feedback-valuable-input"
                    className={`${inputCls} resize-y leading-relaxed`}
                  />
                </div>

                <div>
                  <label htmlFor="feedback-improve" className={labelCls}>What could we improve?</label>
                  <textarea
                    id="feedback-improve"
                    rows={3}
                    value={form.improve}
                    onChange={set("improve")}
                    placeholder="Anything that felt missing, rushed or unclear"
                    data-testid="feedback-improve-input"
                    className={`${inputCls} resize-y leading-relaxed`}
                  />
                </div>

                <div>
                  <label className={labelCls}>Would you recommend it to a colleague? *</label>
                  <div className="flex flex-wrap gap-2.5" data-testid="feedback-recommend">
                    {RECOMMEND.map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, recommend: r.key }))}
                        data-testid={`feedback-recommend-${r.key}`}
                        className={`px-5 py-2.5 rounded-full text-sm font-semibold border transition-colors duration-200 ${
                          form.recommend === r.key
                            ? "bg-tnn-purple text-white border-tnn-purple"
                            : "bg-white text-tnn-muted border-tnn-ink/15 hover:border-tnn-purple hover:text-tnn-purple"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="feedback-testimonial" className={labelCls}>A sentence we could share? (optional)</label>
                  <textarea
                    id="feedback-testimonial"
                    rows={2}
                    value={form.testimonial}
                    onChange={set("testimonial")}
                    placeholder="If something shifted for you, we would love to quote you"
                    data-testid="feedback-testimonial-input"
                    className={`${inputCls} resize-y leading-relaxed`}
                  />
                  <label className="mt-3 flex items-center gap-2.5 text-sm text-tnn-muted cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.testimonial_consent}
                      onChange={(e) => setForm((f) => ({ ...f, testimonial_consent: e.target.checked }))}
                      data-testid="feedback-consent-checkbox"
                      className="w-4 h-4 accent-tnn-purple"
                    />
                    You may quote me (first name only) on the TNN Africa site
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="feedback-name" className={labelCls}>Name (optional)</label>
                    <input
                      id="feedback-name"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Your first name"
                      data-testid="feedback-name-input"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="feedback-email" className={labelCls}>Email (optional)</label>
                    <input
                      id="feedback-email"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="Only if you want a reply"
                      data-testid="feedback-email-input"
                      className={inputCls}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="feedback-submit-button"
                  className="group w-full inline-flex items-center justify-center gap-2 bg-tnn-purple text-white font-bold text-sm tracking-wide px-8 py-4 rounded-full transition-colors duration-200 hover:bg-tnn-deep disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> SENDING
                    </>
                  ) : (
                    <>
                      SEND FEEDBACK
                      <Check className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
                    </>
                  )}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
